import { useCallback, useEffect, useState } from 'react';
import {
  type MemberRole,
  type PendingInvite,
  type TeamMember,
  cancelInvite,
  inviteMember,
  listPendingInvites,
  listTeamMembers,
  removeMember,
  resendInvite,
  updateMemberRole,
} from '../services/api';
import { useT } from '../i18n';

// Issue #328: pagina Team/Members.
// Acessivel apenas para admin/super_user (guard feito no App.tsx antes de
// montar esta pagina). Lista membros da org, permite convidar, alterar role
// e remover com confirmacao.

const ROLE_OPTIONS: MemberRole[] = ['admin', 'member', 'contador'];

function roleLabel(role: string, t: (k: string) => string): string {
  if (role === 'admin' || role === 'super_user' || role === 'owner') {
    return t('team.role.admin');
  }
  if (role === 'contador') return t('team.role.contador');
  return t('team.role.member');
}

function statusLabel(status: string, t: (k: string) => string): string {
  if (status === 'active' || status === 'invited' || status === 'disabled') {
    return t(`team.status.${status}`);
  }
  return status || t('team.status.active');
}

function statusBadgeClass(status: string): string {
  if (status === 'invited') return 'bg-amber-100 text-amber-700';
  if (status === 'disabled') return 'bg-red-100 text-red-700';
  return 'bg-green-100 text-green-700';
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
}


function Team() {
  const t = useT();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showSpamNotice, setShowSpamNotice] = useState(false);

  // Modal de convite
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<MemberRole>('member');
  const [inviteBusy, setInviteBusy] = useState(false);

  // Confirmacao de remocao
  const [removeTarget, setRemoveTarget] = useState<TeamMember | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);

  // Role em edicao (para feedback inline)
  const [savingRoleId, setSavingRoleId] = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listTeamMembers();
      setMembers(res.members ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('team.error.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchInvites = useCallback(async () => {
    try {
      const data = await listPendingInvites();
      setInvites(data.invites ?? []);
    } catch (err) {
      console.error("[Team] fetchInvites falhou:", err);
      // Nao sobrescrever erro principal de membros, mas logar para debug
    }
  }, []);

  useEffect(() => {
    fetchMembers();
    fetchInvites();
  }, [fetchMembers, fetchInvites]);

  const handleInvite = useCallback(async () => {
    if (!inviteEmail.trim()) return;
    setInviteBusy(true);
    setError(null);
    setSuccess(null);
    setShowSpamNotice(false);
    try {
      await inviteMember(inviteEmail.trim(), inviteRole);
      setShowInvite(false);
      setInviteEmail('');
      setInviteRole('member');
      setSuccess(t('team.invite.success'));
      setShowSpamNotice(true);
      fetchMembers();
      fetchInvites();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('team.invite.error'));
    } finally {
      setInviteBusy(false);
    }
  }, [inviteEmail, inviteRole, fetchMembers, fetchInvites, t]);

  const handleCancelInvite = useCallback(async (inviteId: string) => {
    if (!window.confirm(t('team.invite.cancel.confirmBody'))) return;
    setError(null);
    setSuccess(null);
    setShowSpamNotice(false);
    try {
      await cancelInvite(inviteId);
      setSuccess(t('team.invite.cancel.success'));
      fetchInvites();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('team.invite.cancel.error'));
    }
  }, [fetchInvites, t]);

  const handleResendInvite = useCallback(async (inviteId: string) => {
    setError(null);
    setSuccess(null);
    setShowSpamNotice(false);
    try {
      await resendInvite(inviteId);
      setSuccess(t('team.invite.resend.success'));
      setShowSpamNotice(true);
      fetchInvites();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('team.invite.resend.error'));
    }
  }, [fetchInvites, t]);

  const handleRoleChange = useCallback(async (member: TeamMember, role: MemberRole) => {
    setSavingRoleId(member.id);
    setError(null);
    setSuccess(null);
    setShowSpamNotice(false);
    try {
      await updateMemberRole(member.id, role);
      setMembers((prev) =>
        prev.map((m) => (m.id === member.id ? { ...m, role } : m)),
      );
      setSuccess(t('team.role.updated'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('team.role.error'));
    } finally {
      setSavingRoleId(null);
    }
  }, [t]);

  const handleRemove = useCallback(async () => {
    if (!removeTarget) return;
    setRemoveBusy(true);
    setError(null);
    setSuccess(null);
    setShowSpamNotice(false);
    try {
      await removeMember(removeTarget.id);
      setSuccess(t('team.remove.success'));
      setRemoveTarget(null);
      fetchMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('team.remove.error'));
    } finally {
      setRemoveBusy(false);
    }
  }, [removeTarget, fetchMembers, t]);

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-brand-900">{t('team.title')}</h1>
            <p className="text-sm text-brand-600">{t('team.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={() => setShowInvite(true)}
            className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800"
          >
            {t('team.invite.button')}
          </button>
        </div>

        {error && (
          <p className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}
        {success && (
          <div className="mb-4 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">
            <p>{success}</p>
            {showSpamNotice && (
              <p className="mt-1 text-sm text-green-600">{t('common.checkSpamThirdParty')}</p>
            )}
          </div>
        )}

        {loading ? (
          <p className="text-sm text-brand-600">{t('team.loading')}</p>
        ) : members.length === 0 ? (
          <p className="text-sm text-brand-500">{t('team.empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-brand-200 text-brand-700">
                <tr>
                  <th className="py-2 pr-4">{t('team.col.name')}</th>
                  <th className="py-2 pr-4">{t('team.col.email')}</th>
                  <th className="py-2 pr-4">{t('team.col.role')}</th>
                  <th className="py-2 pr-4">{t('team.col.status')}</th>
                  <th className="py-2 pr-4">{t('team.col.joined')}</th>
                  <th className="py-2 pr-4">{t('team.col.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-b border-brand-100">
                    <td className="py-2 pr-4 text-brand-800">{m.name || m.email}</td>
                    <td className="py-2 pr-4 text-brand-600">{m.email}</td>
                    <td className="py-2 pr-4">
                      <select
                        value={m.role === 'super_user' || m.role === 'owner' ? 'admin' : m.role}
                        onChange={(e) => handleRoleChange(m, e.target.value as MemberRole)}
                        disabled={savingRoleId === m.id}
                        className="rounded-md border border-brand-300 px-2 py-1 text-sm text-brand-800 disabled:opacity-50"
                      >
                        {ROLE_OPTIONS.map((r) => (
                          <option key={r} value={r}>
                            {roleLabel(r, t)}
                          </option>
                        ))}
                      </select>
                      {savingRoleId === m.id && (
                        <span className="ml-2 text-xs text-brand-400">{t('team.saving')}</span>
                      )}
                    </td>
                    <td className="py-2 pr-4">
                      <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ${statusBadgeClass(m.status)}`}>
                        {statusLabel(m.status, t)}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-xs text-brand-500">{formatDate(m.created_at)}</td>
                    <td className="py-2 pr-4">
                      <button
                        type="button"
                        onClick={() => setRemoveTarget(m)}
                        className="rounded-md border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                      >
                        {t('team.remove.button')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Convites Pendentes (Issue #413) */}
      <div className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
        <h3 className="mb-4 text-lg font-semibold text-brand-900">{t('team.invites.title')}</h3>
        {invites.length === 0 ? (
          <p className="text-sm text-brand-500">{t('team.invites.empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-brand-200 text-brand-700">
                <tr>
                  <th className="py-2 pr-4">{t('team.col.email')}</th>
                  <th className="py-2 pr-4">{t('team.col.role')}</th>
                  <th className="py-2 pr-4">{t('team.invites.col.sentAt')}</th>
                  <th className="py-2 pr-4">{t('team.invites.col.expiresAt')}</th>
                  <th className="py-2 pr-4">{t('team.col.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv.id} className="border-b border-brand-100">
                    <td className="py-2 pr-4 text-brand-600">{inv.email}</td>
                    <td className="py-2 pr-4 text-brand-800">{roleLabel(inv.role, t)}</td>
                    <td className="py-2 pr-4 text-xs text-brand-500">{formatDate(inv.created_at)}</td>
                    <td className="py-2 pr-4 text-xs text-brand-500">{formatDate(inv.expires_at)}</td>
                    <td className="py-2 pr-4">
                      <button
                        type="button"
                        onClick={() => handleResendInvite(inv.id)}
                        className="rounded-md border border-brand-300 px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50"
                      >
                        {t('team.invite.resend.button')}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCancelInvite(inv.id)}
                        className="ml-2 rounded-md border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                      >
                        {t('team.invite.cancel.button')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de convite */}
      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-semibold text-brand-900">{t('team.invite.modalTitle')}</h2>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs text-brand-500">{t('team.invite.email')}</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="nome@empresa.com"
                  className="w-full rounded-md border border-brand-300 px-3 py-2 text-sm text-brand-800"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-brand-500">{t('team.invite.role')}</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as MemberRole)}
                  className="w-full rounded-md border border-brand-300 px-3 py-2 text-sm text-brand-800"
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {roleLabel(r, t)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowInvite(false)}
                className="rounded-md border border-brand-300 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50"
              >
                {t('team.invite.cancel')}
              </button>
              <button
                type="button"
                onClick={handleInvite}
                disabled={inviteBusy || !inviteEmail.trim()}
                className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50"
              >
                {inviteBusy ? t('team.saving') : t('team.invite.send')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmacao de remocao */}
      {removeTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-2 text-lg font-semibold text-brand-900">{t('team.remove.confirmTitle')}</h2>
            <p className="mb-4 text-sm text-brand-600">
              {t('team.remove.confirmBody', { email: removeTarget.email })}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setRemoveTarget(null)}
                className="rounded-md border border-brand-300 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50"
              >
                {t('team.remove.cancel')}
              </button>
              <button
                type="button"
                onClick={handleRemove}
                disabled={removeBusy}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {removeBusy ? t('team.saving') : t('team.remove.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Team;
